import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';

import { useHistory } from 'react-router-dom';

import { Box } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';

import VisibilityIcon from '@mui/icons-material/Visibility';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { useTranslation } from 'react-i18next';

import Swal from 'sweetalert2';

import { SCUIContent } from '../../atomic/organisms';

import { SCUIBreadCrumb } from '../../atomic/molecules';

import { SCUIButton, SCUIPagination } from '../../atomic/atoms';

import RuntimeConfigurationQuery from '../../services/query/runtimeConfiguration/runtimeConfigurationQuery.services';

import RuntimeConfigurationCommand from '../../services/command/runtimeConfiguration/runtimeConfigurationCommand.services';

import { PageUrl } from '../../types/enums/pageUrl-enums';

import {
    buildRuntimeConfigurationDetailUrl,
    getRuntimeConfigurationOperationMessage,
    getValueTypeTranslationKey,
    isRuntimeConfigurationOperationFailed,
} from './runtimeConfiguration.helpers';

import { RuntimeConfigurationListItem } from './runtimeConfiguration.types';

import { RuntimeConfigurationTypeBadge } from './components/RuntimeConfigurationTypeBadge';

import './runtime-configuration-pages.scss';



const DEFAULT_PAGE_SIZE = 20;



export const RuntimeConfigurationList: FC = () => {

    const { t } = useTranslation();

    const history = useHistory();

    const [items, setItems] = useState<RuntimeConfigurationListItem[]>([]);

    const [totalCount, setTotalCount] = useState(0);

    const [page, setPage] = useState(0);

    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

    const [loading, setLoading] = useState(false);



    const loadData = useCallback(async () => {

        setLoading(true);

        try {

            const data = await RuntimeConfigurationQuery.ListRuntimeConfigurations({

                pageNumber: page + 1,

                pageSize: rowsPerPage,

            });

            setItems(data.items);

            setTotalCount(data.totalCount);

        } finally {

            setLoading(false);

        }

    }, [page, rowsPerPage]);



    useEffect(() => {

        loadData();

    }, [loadData]);



    const goToDetail = useCallback(

        (configKey: string, version?: number) => {

            history.push(buildRuntimeConfigurationDetailUrl(configKey, version));

        },

        [history]

    );



    const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {

        setRowsPerPage(Number.parseInt(event.target.value, 10));

        setPage(0);

    };



    const handleDelete = async (configKey: string) => {

        const result = await Swal.fire({

            icon: 'warning',

            title: t('runtimeConfiguration.list.deleteConfirmTitle'),

            text: t('runtimeConfiguration.list.deleteConfirmText', { key: configKey }),

            showCancelButton: true,

            confirmButtonText: t('general.yes'),

            cancelButtonText: t('general.cancel'),

        });



        if (!result.isConfirmed) return;



        try {

            const deleteResult = await RuntimeConfigurationCommand.DeleteRuntimeConfiguration(configKey);

            if (isRuntimeConfigurationOperationFailed(deleteResult)) {
                Swal.fire({
                    icon: 'error',
                    title: t('general.error'),
                    text: getRuntimeConfigurationOperationMessage(deleteResult, t('general.error')),
                });
                return;
            }

            await Swal.fire({

                icon: 'success',

                title: t('general.success'),

                timer: 1500,

                showConfirmButton: false,

            });

            await loadData();

        } catch (error: unknown) {

            const message = error instanceof Error ? error.message : t('general.error');

            Swal.fire({ icon: 'error', title: t('general.error'), text: message });

        }

    };



    const rangeStart = totalCount === 0 ? 0 : page * rowsPerPage + 1;

    const rangeEnd = Math.min(totalCount, (page + 1) * rowsPerPage);



    return (

        <SCUIContent>

            <Box className="rc-page">

                <Box className="rc-page__breadcrumb">

                    <SCUIBreadCrumb

                        onlyTitle={t('runtimeConfiguration.list.pageTitle')}

                        components={[

                            { title: t('runtimeConfiguration.title'), link: PageUrl.runtimeConfiguration.url },

                            { title: t('runtimeConfiguration.list.pageTitle') },

                        ]}

                    />

                </Box>



                <Box className="rc-page__header">

                    <Box>

                        <h1 className="rc-page__title">{t('runtimeConfiguration.list.pageTitle')}</h1>

                        <p className="rc-page__subtitle">{t('runtimeConfiguration.list.pageSubtitle')}</p>

                    </Box>

                    <Box className="rc-page__header-actions">

                        <SCUIButton

                            type="primary"

                            text={t('runtimeConfiguration.list.create')}

                            showStartIcon

                            startIcon={<AddIcon sx={{ fontSize: 18 }} />}

                            onClick={() => history.push(PageUrl.runtimeConfigurationCreate.url)}

                        />

                    </Box>

                </Box>



                <Box className="rc-card">

                    <Box className="rc-table-wrap">

                        <table className="rc-table">

                            <thead>

                                <tr>

                                    <th>{t('runtimeConfiguration.list.key')}</th>

                                    <th>{t('runtimeConfiguration.list.displayName')}</th>

                                    <th>{t('runtimeConfiguration.list.valueType')}</th>

                                    <th>{t('runtimeConfiguration.list.currentVersion')}</th>

                                    <th>{t('runtimeConfiguration.list.updatedBy')}</th>

                                    <th>{t('runtimeConfiguration.list.updatedDate')}</th>

                                    <th style={{ textAlign: 'right' }}>{t('runtimeConfiguration.list.actions')}</th>

                                </tr>

                            </thead>

                            <tbody>

                                {!loading && items.length === 0 && (

                                    <tr>

                                        <td colSpan={7}>

                                            <Box className="rc-empty-state">{t('runtimeConfiguration.list.empty')}</Box>

                                        </td>

                                    </tr>

                                )}

                                {items.map((row) => (

                                    <tr key={row.id}>

                                        <td>

                                            <button

                                                type="button"

                                                className="rc-key-link"

                                                onClick={() => goToDetail(row.key, row.currentVersion)}

                                            >

                                                <span>{row.key}</span>

                                                <OpenInNewIcon sx={{ fontSize: 14, color: '#94a3b8' }} />

                                            </button>

                                        </td>

                                        <td>{row.displayName}</td>

                                        <td>

                                            <RuntimeConfigurationTypeBadge

                                                valueType={row.valueType}

                                                label={t(getValueTypeTranslationKey(row.valueType))}

                                            />

                                        </td>

                                        <td>

                                            <span className="rc-version-tag">v{row.currentVersion}</span>

                                        </td>

                                        <td style={{ fontSize: '0.75rem', color: '#475569' }}>{row.updatedBy || '—'}</td>

                                        <td style={{ fontSize: '0.75rem', color: '#64748b' }}>

                                            {row.updatedDate ? new Date(row.updatedDate).toLocaleString() : '—'}

                                        </td>

                                        <td>

                                            <Box className="rc-table-actions">

                                                <button

                                                    type="button"

                                                    className="rc-icon-btn"

                                                    title={t('runtimeConfiguration.list.openDetail')}

                                                    onClick={() => goToDetail(row.key, row.currentVersion)}

                                                >

                                                    <VisibilityIcon sx={{ fontSize: 18 }} />

                                                </button>

                                                <button

                                                    type="button"

                                                    className="rc-icon-btn rc-icon-btn--danger"

                                                    title={t('general.delete')}

                                                    onClick={() => handleDelete(row.key)}

                                                >

                                                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />

                                                </button>

                                            </Box>

                                        </td>

                                    </tr>

                                ))}

                            </tbody>

                        </table>

                    </Box>

                    <Box className="rc-table-footer rc-table-footer--pagination">

                        <span>

                            {t('runtimeConfiguration.list.footerSummaryPaged', {

                                start: rangeStart,

                                end: rangeEnd,

                                total: totalCount,

                            })}

                        </span>

                        <SCUIPagination

                            rowsPerPageOptions={[10, 20, 50]}

                            count={totalCount}

                            rowsPerPage={rowsPerPage}

                            page={page}

                            onRowsPerPageChange={handleRowsPerPageChange}

                            setPage={setPage}

                        />

                    </Box>

                </Box>

            </Box>

        </SCUIContent>

    );

};

